import React, {useEffect, useState} from 'react';
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Editor} from "primereact/editor";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {TemplateService} from "../../service/TemplateService";
import {ProductExcelImportService} from "../../service/ProductExcelImportService";
import {DashboardService} from "../../service/DashboardService";
import {CampaignService} from "../../service/CampaignService";
import {exportFile} from "../../utils/Utils";
import {ExcelOpsCard} from "./ExcelOps";

const templateService = new TemplateService();
const productExcelImportService = new ProductExcelImportService();
const dashboardService = new DashboardService();
const campaignService = new CampaignService();

const MapOnboardingStep = ({t, embedded}) => {
    const {toast, setError} = React.useContext(AppContext);
    const {selectedCampaign, loadCampaigns} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;

    const [overview, setOverview] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [emailText, setEmailText] = useState('');
    const [savingEmail, setSavingEmail] = useState(false);

    const openEmail = () => {
        setEmailText(selectedCampaign?.settings?.violation_email_text || '');
        setShowEmail(true);
    };
    const saveEmail = () => {
        setSavingEmail(true);
        const merged = {...(selectedCampaign.settings || {}), violation_email_text: emailText};
        campaignService.saveCampaignSettings(campaignId, selectedCampaign.use_sale_value, merged)
            .then(() => {
                selectedCampaign.settings = merged;
                if (loadCampaigns) loadCampaigns();
                toast.current.show({severity: 'success', summary: t('campaign.violation_email_text', 'İhlal E-posta Metni'), detail: t('message_detail.successful')});
                setShowEmail(false);
            })
            .catch(error => { setError(error); setError(null); })
            .finally(() => setSavingEmail(false));
    };

    const loadOverview = () => {
        if (!campaignId) return;
        setMetricsLoading(true);
        dashboardService.getOverview(campaignId).then(o => setOverview(o || null))
            .catch(() => {}).finally(() => setMetricsLoading(false));
    };
    useEffect(() => { loadOverview(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            {!embedded && <>
                <h2 style={{margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10}}>
                    <i className="pi pi-shield"/> {t('onboarding.step.map.label', 'MAP')}
                </h2>
                <p style={{color: '#6b7280', maxWidth: 640, marginTop: 0}}>{t('onboarding.step.map.desc')}</p>
            </>}

            {/* Metrics strip */}
            {(() => {
                const o = overview || {};
                const tiles = [
                    {key: 'mapViolation', value: o.total_violated_map, color: '#dc2626', icon: 'pi pi-exclamation-triangle'},
                    {key: 'mrpViolation', value: o.total_violated_mrp, color: '#f59e0b', icon: 'pi pi-exclamation-circle'},
                    {key: 'noViolation', value: o.total_no_violated_map, color: '#16a34a', icon: 'pi pi-check-circle'},
                    {key: 'noDefinedMap', value: o.total_no_defined_map, color: '#9ca3af', icon: 'pi pi-shield'},
                    {key: 'noReseller', value: o.total_products_without_competitors, color: '#6b7280', icon: 'pi pi-minus-circle'},
                ];
                return (
                    <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, marginBottom: 16}}>
                        {tiles.map(tile => (
                            <div key={tile.key} style={{flex: '1 1 150px', minWidth: 140, border: '1px solid #e5e7eb',
                                borderRadius: 10, padding: '12px 14px', background: '#fff'}}>
                                <div style={{fontSize: '1.6rem', fontWeight: 800, color: tile.color, display: 'flex', alignItems: 'center', gap: 8}}>
                                    <i className={tile.icon} style={{fontSize: '1.1rem'}}/>
                                    {metricsLoading ? '…' : (tile.value ?? 0)}
                                </div>
                                <small style={{color: '#6b7280'}}>{t('onboarding.map.metrics.' + tile.key)}</small>
                            </div>
                        ))}
                    </div>
                );
            })()}

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14}}>
                <ExcelOpsCard t={t} icon="pi pi-file-excel" title={t('actions.update_product_map')} hint={t('onboarding.map.excelHint')}
                              download={() => templateService.exportProductMapUpdateTemplate().then(r => exportFile(r, 'product_map_updating_template.xlsx'))}
                              uploads={[{labelKey: 'actions.update_product_map',
                                  run: (file) => productExcelImportService.updateProductMaps(campaignId, file).then(loadOverview)}]}/>

                <ExcelOpsCard t={t} icon="pi pi-calendar" title={t('actions.import_violation_strategy_schedule')}
                              hint={t('onboarding.map.violationScheduleHint')}
                              download={() => templateService.exportViolationStrategyScheduleTemplate().then(r => exportFile(r, 'violation_strategy_schedule_template.xlsx'))}
                              uploads={[{labelKey: 'actions.import_violation_strategy_schedule',
                                  run: (file) => productExcelImportService.importViolationStrategySchedule(campaignId, file).then(loadOverview)}]}/>

                {/* Violation notification email template (shown whenever the MAP step is) */}
                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff',
                    display: 'flex', flexDirection: 'column', gap: 8}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                        <i className="pi pi-envelope" style={{color: '#2563eb'}}/> {t('campaign.violation_email_text', 'İhlal E-posta Metni')}
                    </div>
                    <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.map.violationEmailHint', 'MAP/MRP ihlali yapan satıcılara gönderilecek e-posta metni.')}</small>
                    <Button label={t('actions.edit', 'Düzenle')} icon="pi pi-pencil" className="p-button-outlined" onClick={openEmail}/>
                </div>
            </div>

            <Dialog header={t('campaign.violation_email_text', 'İhlal E-posta Metni')} visible={showEmail}
                    style={{width: '760px', maxWidth: '95vw'}} modal onHide={() => !savingEmail && setShowEmail(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={savingEmail}
                                    onClick={() => setShowEmail(false)}/>
                            <Button label={t('actions.save', 'Kaydet')} icon="pi pi-save" loading={savingEmail} onClick={saveEmail}/>
                        </div>}>
                <Editor value={emailText} onTextChange={(e) => setEmailText(e.htmlValue || '')} style={{height: '420px'}}/>
            </Dialog>
        </div>
    );
};

export default MapOnboardingStep;
